import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import { verifyEmailWithToken } from '../../graphqlService';
import { debugLog } from '../../utils/debugLog';
import AuthLayout from './AuthLayout';
import AuthStatus, { AuthStatusAction, AuthStatusTone } from './AuthStatus';
import { isNetworkFailure, NETWORK_ERROR_MESSAGE } from './authErrors';
import { SUPPORT_HREF } from './authForm';
import { hasSignedTokenExpired } from './authLinks';

export const ALREADY_VERIFIED_MESSAGE = 'This email address is already verified. You can sign in.';

/**
 * The backend answers success for a link whose address was verified before
 * (an old link, or the same link opened twice), and says so only in its
 * message. Celebrating that as a newly activated account was misleading.
 */
const isAlreadyVerifiedResult = (result: { error?: string | null; message?: string | null }) =>
  result.error === 'ALREADY_VERIFIED' || /already verified/i.test(result.message || '');

type VerifyProblem = 'incomplete' | 'invalid' | 'expired' | 'no-account' | 'server' | 'network';

type LandingState =
  | { kind: 'verifying' }
  | { kind: 'verified'; alreadyVerified: boolean }
  | { kind: 'failed'; problem: VerifyProblem };

/** Error codes from the API's verifyEmailWithToken. */
const problemFromCode = (code: string | null | undefined, token: string): VerifyProblem => {
  switch (code) {
    case 'TOKEN_EXPIRED':
      return 'expired';
    case 'INVALID_TOKEN':
      // The API can't tell an expired signed link from a broken one.
      return hasSignedTokenExpired(token) ? 'expired' : 'invalid';
    case 'WRONG_TOKEN_TYPE':
    case 'MISSING_TOKEN_FIELDS':
    case 'EMAIL_MISMATCH':
      return 'invalid';
    case 'USER_NOT_FOUND':
      return 'no-account';
    default:
      return 'server';
  }
};

const EmailVerificationLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<LandingState>({ kind: 'verifying' });
  const [attempt, setAttempt] = useState(0);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    let cancelled = false;

    const verifyEmail = async () => {
      if (!token || !email) {
        setState({ kind: 'failed', problem: 'incomplete' });
        return;
      }

      try {
        debugLog('Verifying email with token...');
        const result = await verifyEmailWithToken(token, email);
        if (cancelled) return;

        if (result?.success) {
          setState({ kind: 'verified', alreadyVerified: isAlreadyVerifiedResult(result) });
        } else {
          setState({ kind: 'failed', problem: problemFromCode(result?.error, token) });
        }
      } catch (error: unknown) {
        console.error('Verification error:', error);
        if (!cancelled) setState({ kind: 'failed', problem: isNetworkFailure(error) ? 'network' : 'server' });
      }
    };

    // A short pause on first load, so the result doesn't flash past.
    const timer = setTimeout(verifyEmail, attempt === 0 ? 1000 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, email, attempt]);

  const handleContinueToLogin = () => {
    navigate('/login', {
      state: {
        message:
          state.kind === 'verified' && state.alreadyVerified
            ? ALREADY_VERIFIED_MESSAGE
            : 'Email verified. You can now sign in.',
        emailVerified: true,
      },
    });
  };

  const handleRequestNewLink = () => {
    navigate('/login', {
      state: {
        message: 'Sign in to get a new verification email.',
        needsVerification: true,
      },
    });
  };

  const retry = () => {
    setState({ kind: 'verifying' });
    setAttempt((count) => count + 1);
  };

  if (state.kind === 'verifying') {
    return (
      <AuthLayout>
        <Box role="status">
          <AuthStatus
            tone="info"
            icon={<CircularProgress size={22} color="inherit" />}
            title="Verifying your email"
            description="This only takes a moment."
          />
        </Box>
      </AuthLayout>
    );
  }

  if (state.kind === 'verified') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="success"
          title={state.alreadyVerified ? 'Email already verified' : 'Email verified'}
          description={
            state.alreadyVerified
              ? ALREADY_VERIFIED_MESSAGE
              : 'Your account is now active. Sign in to get started.'
          }
          primaryAction={{ label: 'Continue to sign in', onClick: handleContinueToLogin }}
        />
      </AuthLayout>
    );
  }

  const requestNewLink: AuthStatusAction = { label: 'Request a new link', onClick: handleRequestNewLink };
  const backToSignIn: AuthStatusAction = { label: 'Back to sign in', to: '/login' };

  const failures: Record<
    VerifyProblem,
    { tone: AuthStatusTone; icon?: React.ReactNode; title: string; description: string; primary: AuthStatusAction }
  > = {
    incomplete: {
      tone: 'error',
      icon: <LinkOffOutlinedIcon />,
      title: "This verification link isn't complete",
      description: 'Open the link from your email again, or request a new one.',
      primary: requestNewLink,
    },
    invalid: {
      tone: 'error',
      icon: <LinkOffOutlinedIcon />,
      title: "This verification link isn't valid",
      description: 'It may be incomplete or meant for a different address. Request a new verification email.',
      primary: requestNewLink,
    },
    expired: {
      tone: 'warning',
      icon: <TimerOffOutlinedIcon />,
      title: 'This verification link has expired',
      description: 'Verification links work for 24 hours. Request a new one to activate your account.',
      primary: requestNewLink,
    },
    'no-account': {
      tone: 'error',
      title: "We couldn't find your account",
      description: "Contact support and we'll help you get set up.",
      primary: { label: 'Contact support', href: SUPPORT_HREF },
    },
    server: {
      tone: 'error',
      title: "We couldn't verify your email",
      description: 'Something went wrong on our side. Try again in a few minutes.',
      primary: { label: 'Try again', onClick: retry },
    },
    network: {
      tone: 'error',
      title: "We couldn't verify your email",
      description: NETWORK_ERROR_MESSAGE,
      primary: { label: 'Try again', onClick: retry },
    },
  };
  const failure = failures[state.problem];

  return (
    <AuthLayout>
      <AuthStatus
        tone={failure.tone}
        icon={failure.icon}
        title={failure.title}
        description={failure.description}
        primaryAction={failure.primary}
        secondaryAction={backToSignIn}
      />
    </AuthLayout>
  );
};

export default EmailVerificationLanding;
